import { History, Users } from 'lucide-react';
import type { PaginatedResponse, Profile } from '@/api/admin/profiles';
import { DateTimeCell } from '@/components/table/cells';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ProfileTableProps {
  data: PaginatedResponse<Profile>;
  currentPage: number;
  onPageChange: (page: number) => void;
  onViewHistory: (profile: Profile) => void;
  onViewUsers: (profile: Profile) => void;
  onToggleActive: (profile: Profile) => void;
  canToggleActive?: boolean;
  canView?: boolean;
}

export function ProfileTable({
  data,
  currentPage,
  onPageChange,
  onViewHistory,
  onViewUsers,
  onToggleActive,
  canToggleActive = true,
  canView = true,
}: ProfileTableProps) {
  const docs = data.docs ?? [];

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Permissões</TableHead>
                <TableHead>Data de criação</TableHead>
                <TableHead className="w-[100px]">Ações</TableHead>
                <TableHead className="w-[150px] whitespace-nowrap text-center">
                  Ativar / Desativar
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Nenhum perfil encontrado
                  </TableCell>
                </TableRow>
              ) : (
                docs.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell>
                      <span className="font-medium">{profile.name}</span>
                    </TableCell>
                    <TableCell>
                      {profile.active ? (
                        <Badge variant="success-subtle">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary-subtle">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">
                        {profile.locked_control_code?.length || 0} permissões
                      </span>
                    </TableCell>
                    <TableCell>
                      <DateTimeCell date={profile.created_at} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-3 items-center">
                        {canView && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onViewHistory(profile)}
                                className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              >
                                <History className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Histórico</TooltipContent>
                          </Tooltip>
                        )}

                        {canView && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onViewUsers(profile)}
                                className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Usuários</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => canToggleActive && onToggleActive(profile)}
                            disabled={!canToggleActive}
                            className={cn(
                              'cursor-pointer',
                              !canToggleActive && 'cursor-not-allowed opacity-50',
                            )}
                          >
                            <Switch checked={profile.active} className="pointer-events-none" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {canToggleActive
                            ? profile.active
                              ? 'Inativar perfil'
                              : 'Ativar perfil'
                            : 'Sem permissão'}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={data.pages}
          totalItems={data.total}
          limit={data.limit}
          onPageChange={onPageChange}
        />
      </div>
    </TooltipProvider>
  );
}
